'use client'

import { ObjectId } from 'bson'

const MENTION_PATTERN = /\{(\{.*?\})\}/g
const OBJECT_ID_RE = /^[a-f0-9]{24}$/

export function generateObjectId() {
    return new ObjectId().toHexString()
}

export function computeChecksum(text) {
    let hash = 0
    for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash
    }
    return (hash >>> 0).toString(16).padStart(8, '0')
}

function isValidId(id) {
    return typeof id === 'string' && OBJECT_ID_RE.test(id)
}

/**
 * Extracts the title text that follows a mention in a serialized script.
 * The title spans from right after the mention to the first empty line.
 * Mention tokens within the title range are stripped out.
 */
export function getTitleAfterMention(script, mentionEndIndex) {
    const rest = script.slice(mentionEndIndex)
    const lines = rest.split('\n')

    let started = false
    const titleParts = []
    for (const line of lines) {
        const stripped = line.replace(MENTION_PATTERN, '').trim()
        if (!started) {
            if (stripped === '') continue
            started = true
        } else if (stripped === '') {
            break
        }
        titleParts.push(stripped)
    }

    return titleParts.join(' ').trim()
}

/**
 * Extracts segment boundaries and titles from a serialized script string.
 * Returns an array of { id, label, start, end }.
 */
export function extractSegments(script) {
    if (!script) return []

    const segments = []
    const pattern = new RegExp(MENTION_PATTERN.source, 'g')
    let match

    while ((match = pattern.exec(script)) !== null) {
        try {
            const data = JSON.parse(match[1])
            if (data.type === 'segment') {
                const mentionEnd = match.index + match[0].length
                const title = getTitleAfterMention(script, mentionEnd)
                segments.push({
                    id: isValidId(data.id) ? data.id : generateObjectId(),
                    label: title || 'New Segment',
                    start: match.index,
                })
            }
        } catch { /* not valid JSON, skip */ }
    }

    if (segments.length === 0) return []

    if (segments[0].start > 0) {
        const preambleText = script.slice(0, segments[0].start).trim()
        if (preambleText.length > 0) {
            segments.unshift({
                id: '__preamble__',
                label: 'Preamble',
                start: 0,
            })
        }
    }

    for (let i = 0; i < segments.length; i++) {
        segments[i].end = i < segments.length - 1
            ? segments[i + 1].start
            : script.length
    }

    return segments
}

/**
 * Splices an edited segment back into the full script string.
 */
export function spliceSegment(fullScript, segmentStart, segmentEnd, newContent) {
    return fullScript.slice(0, segmentStart) + newContent + fullScript.slice(segmentEnd)
}
